import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, KeyRound, BellRing } from "lucide-react";
import { GlassCard, RoleBadge } from "../../components/common/ui";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import api from "../../lib/api";
import { useAuth, formatApiError } from "../../context/AuthContext";

const fmt = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "—");

export default function Profile() {
  const { user, setUser, refresh } = useAuth();
  const parts = (user?.name || "").trim().split(" ");
  const [firstName, setFirstName] = useState(user?.first_name || parts[0] || "");
  const [lastName, setLastName] = useState(user?.last_name || parts.slice(1).join(" ") || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [consent, setConsent] = useState(false);
  const [nl, setNl] = useState(null);

  const [curPw, setCurPw] = useState(""); const [newPw, setNewPw] = useState(""); const [savingPw, setSavingPw] = useState(false);

  const [newEmail, setNewEmail] = useState(""); const [emailPw, setEmailPw] = useState("");
  const [otp, setOtp] = useState(""); const [pendingEmail, setPendingEmail] = useState(""); const [emailBusy, setEmailBusy] = useState(false);

  const isEmailAccount = user?.auth_provider === "email";

  useEffect(() => { api.get("/me/newsletter").then(({ data }) => { setNl(data); setConsent(!!data.marketing_consent); }).catch(() => {}); }, []);

  const saveProfile = async (e) => {
    e.preventDefault(); setSavingProfile(true);
    try {
      const { data } = await api.patch("/me/profile", { first_name: firstName, last_name: lastName, phone });
      setUser(data); toast.success("Profile updated.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const toggleConsent = async (val) => {
    setConsent(val);
    try { const { data } = await api.post("/me/newsletter", { marketing_consent: val }); setNl(data); toast.success(val ? "Subscribed to VIP updates." : "Unsubscribed."); }
    catch (err) { setConsent(!val); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changePw = async (e) => {
    e.preventDefault(); setSavingPw(true);
    try { await api.post("/me/change-password", { current_password: curPw, new_password: newPw }); toast.success("Password updated."); setCurPw(""); setNewPw(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const requestEmail = async (e) => {
    e.preventDefault(); setEmailBusy(true);
    try { const { data } = await api.post("/me/email-change/request", { new_email: newEmail, password: emailPw }); setPendingEmail(data.pending_email); toast.success("Verification code sent to your new email."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setEmailBusy(false); }
  };

  const confirmEmail = async (e) => {
    e.preventDefault(); setEmailBusy(true);
    try { const { data } = await api.post("/me/email-change/confirm", { otp }); setUser(data); await refresh(); setPendingEmail(""); setNewEmail(""); setEmailPw(""); setOtp(""); toast.success("Email address updated."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setEmailBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="customer-profile">
      <div>
        <h1 className="font-playfair text-2xl sm:text-3xl text-white">Profile & Account</h1>
        <p className="mt-2 text-muted-foreground">Manage your details, preferences and security.</p>
      </div>

      {/* Account header */}
      <GlassCard className="p-6" hover={false}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm text-white font-semibold flex items-center gap-2 flex-wrap break-all">{user?.email}
              {user?.is_verified && <span className="text-emerald-400 text-xs flex items-center gap-1 whitespace-nowrap"><ShieldCheck className="h-3.5 w-3.5" /> Verified</span>}
            </p>
            <p className="text-xs text-muted-foreground capitalize">Signed in via {user?.auth_provider}</p>
          </div>
          {user?.role && <RoleBadge role={user.role} />}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-white/50 border-t border-white/10 pt-4">
          <p>Account created<br /><span className="text-white/80">{fmt(user?.created_at)}</span></p>
          <p>Last login<br /><span className="text-white/80">{fmt(user?.last_login_at)}</span></p>
        </div>
      </GlassCard>

      {/* Personal details */}
      <GlassCard className="p-6" hover={false}>
        <h2 className="font-playfair text-lg text-white">Personal details</h2>
        <form onSubmit={saveProfile} className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label className="text-white/80">First name</Label><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="profile-first-name" className="bg-navy border-white/15 text-white h-11" /></div>
            <div className="space-y-2"><Label className="text-white/80">Last name</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="profile-last-name" className="bg-navy border-white/15 text-white h-11" /></div>
          </div>
          <div className="space-y-2"><Label className="text-white/80">Mobile number</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07…" data-testid="profile-phone-input" className="bg-navy border-white/15 text-white h-11" /></div>
          <Button type="submit" disabled={savingProfile} className="cf-gold-btn font-semibold h-11" data-testid="profile-save-btn">{savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}</Button>
        </form>
      </GlassCard>

      {/* Newsletter */}
      <GlassCard className="p-6" hover={false} data-testid="profile-newsletter">
        <h2 className="font-playfair text-lg text-white flex items-center gap-2"><BellRing className="h-5 w-5 text-gold" /> VIP newsletter</h2>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-white/80">Receive early access, launch news and exclusive offers.</p>
            {nl?.consent_at && consent && <p className="text-xs text-white/40 mt-1">Subscribed {fmt(nl.consent_at)}</p>}
            {nl?.unsubscribed_at && !consent && <p className="text-xs text-white/40 mt-1">Unsubscribed {fmt(nl.unsubscribed_at)}</p>}
          </div>
          <Switch checked={consent} onCheckedChange={toggleConsent} data-testid="newsletter-toggle" />
        </div>
        <p className="mt-3 text-xs text-white/40">No marketing emails are sent yet — your preference is recorded for launch.</p>
      </GlassCard>

      {/* Security */}
      {isEmailAccount && (
        <GlassCard className="p-6" hover={false} data-testid="profile-security">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><KeyRound className="h-5 w-5 text-gold" /> Change password</h2>
          <form onSubmit={changePw} className="mt-4 space-y-4">
            <div className="space-y-2"><Label className="text-white/80">Current password</Label><Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} required data-testid="current-password" className="bg-navy border-white/15 text-white h-11" /></div>
            <div className="space-y-2"><Label className="text-white/80">New password</Label><Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8} data-testid="new-password" className="bg-navy border-white/15 text-white h-11" /></div>
            <Button type="submit" disabled={savingPw} className="cf-gold-btn font-semibold h-11" data-testid="change-password-btn">{savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}</Button>
          </form>
        </GlassCard>
      )}

      {/* Email change */}
      {isEmailAccount && (
        <GlassCard className="p-6" hover={false} data-testid="profile-email-change">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><Mail className="h-5 w-5 text-gold" /> Change email address</h2>
          <p className="mt-2 text-xs text-white/40">Your new email must be verified before it replaces your current one.</p>
          {!pendingEmail ? (
            <form onSubmit={requestEmail} className="mt-4 space-y-4">
              <div className="space-y-2"><Label className="text-white/80">New email</Label><Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required data-testid="new-email" className="bg-navy border-white/15 text-white h-11" /></div>
              <div className="space-y-2"><Label className="text-white/80">Confirm with your password</Label><Input type="password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} required data-testid="email-change-password" className="bg-navy border-white/15 text-white h-11" /></div>
              <Button type="submit" disabled={emailBusy} className="cf-gold-btn font-semibold h-11" data-testid="request-email-change-btn">{emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Verification Code"}</Button>
            </form>
          ) : (
            <form onSubmit={confirmEmail} className="mt-4 space-y-4">
              <p className="text-sm text-white/70">Enter the code sent to <strong>{pendingEmail}</strong>.</p>
              <div className="space-y-2"><Label className="text-white/80">Verification code</Label><Input value={otp} onChange={(e) => setOtp(e.target.value)} required data-testid="email-change-otp" className="bg-navy border-white/15 text-white h-11" /></div>
              <div className="flex gap-2">
                <Button type="submit" disabled={emailBusy} className="cf-gold-btn font-semibold h-11" data-testid="confirm-email-change-btn">{emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm New Email"}</Button>
                <Button type="button" variant="ghost" className="text-white/60 h-11" onClick={() => setPendingEmail("")}>Cancel</Button>
              </div>
            </form>
          )}
        </GlassCard>
      )}
    </div>
  );
}
